import { useState, useEffect, ChangeEvent } from 'react';
import { Box, Typography, TextField, Button, Alert, Stack } from '@mui/material';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import LoadingSpinner from '@components/LoadingSpinner';
import OnboardingHeader from '../components/onboarding/OnboardingHeader';
import { updatePhoneNumber } from '../services/onboardingService';
import { formatPhoneWithCountryCode } from '../utils/phoneUtils';

interface TranslationStrings {
  title: string;
  emailVerified: string;
  verifyPhone: string;
  phoneNumber: string;
  enterPhoneNumber: string;
  sendVerificationCode: string;
  verificationCode: string;
  verifyCode: string;
  resendCode: string;
  resendCodeWithTimer: string;
  phoneVerificationSuccess: string;
  contactYou: string;
  goToHome: string;
  phoneVerificationFailed: string;
  verificationCodeSent: string;
  pleaseEnterPhone: string;
  pleaseEnterCode: string;
  phoneUpdateWarning: string;
}

interface TranslationsMap {
  zh: TranslationStrings;
  en: TranslationStrings;
  [key: string]: TranslationStrings;
}

const EmailVerificationSuccess: React.FC = () => {
  const [currentLanguage, setCurrentLanguage] = useState<string>('zh');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [userData, setUserData] = useState<{ email?: string } | null>(null);

  const translations: TranslationsMap = {
    zh: {
      title: 'HKStylist 商戶申請',
      emailVerified: '電郵已成功驗證！',
      verifyPhone: '請驗證您的電話號碼以完成註冊',
      phoneNumber: '電話號碼',
      enterPhoneNumber: '請輸入您的電話號碼',
      sendVerificationCode: '發送驗證碼',
      verificationCode: '驗證碼',
      verifyCode: '驗證',
      resendCode: '重發驗證碼',
      resendCodeWithTimer: '重發驗證碼 (%s秒)',
      phoneVerificationSuccess: '感謝您驗證電話號碼。您的帳戶設置現已完成！',
      contactYou: '我們會在三個工作日內就您的申請狀態與您聯絡。',
      goToHome: '返回首頁',
      phoneVerificationFailed: '電話驗證失敗',
      verificationCodeSent: '驗證碼已發送',
      pleaseEnterPhone: '請輸入電話號碼',
      pleaseEnterCode: '請輸入驗證碼',
      phoneUpdateWarning: '電話已驗證，但更新個人資料時出現問題。如有需要，請聯絡客戶支援。'
    },
    en: {
      title: 'HKStylist Merchant Application',
      emailVerified: 'Email Verified Successfully!',
      verifyPhone: 'Please verify your phone number to complete your registration',
      phoneNumber: 'Phone Number',
      enterPhoneNumber: 'Enter your phone number',
      sendVerificationCode: 'Send Verification Code',
      verificationCode: 'Verification Code',
      verifyCode: 'Verify Code',
      resendCode: 'Resend Code',
      resendCodeWithTimer: 'Resend Code (%ss)',
      phoneVerificationSuccess: 'Thank you for verifying your phone number. Your account setup is now complete!',
      contactYou: 'We will contact you within three business days regarding the status of your application.',
      goToHome: 'Go to Home',
      phoneVerificationFailed: 'Phone verification failed',
      verificationCodeSent: 'Verification code sent',
      pleaseEnterPhone: 'Please enter a phone number',
      pleaseEnterCode: 'Please enter verification code',
      phoneUpdateWarning: 'Phone verified, but there was an issue updating your profile. Please contact support if needed.'
    }
  };

  const t = translations[currentLanguage];

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        setUserData(data.user);
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle countdown for resending verification code
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isCodeSent && !isPhoneVerified && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prevCount => prevCount - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isCodeSent, isPhoneVerified, countdown]);

  // Using centralized phone formatting utility from phoneUtils

  const sendVerificationCode = async () => {
    if (!phone) {
      toast.error(t.pleaseEnterPhone);
      return;
    }

    setIsLoading(true);
    try {
      const formattedPhone = formatPhoneWithCountryCode(phone);

      // First update the user's phone number
      const { error: updateError } = await supabase.auth.updateUser({
        phone: formattedPhone
      });

      if (updateError) throw updateError;

      // Then send the verification code
      const { error: otpError } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
      });

      if (otpError) throw otpError;

      setIsCodeSent(true);
      toast.success(t.verificationCodeSent);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t.phoneVerificationFailed;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    sendVerificationCode();
    setCountdown(60);
    setCanResend(false);
  };

  const verifyCode = async () => {
    if (!verificationCode) {
      toast.error(t.pleaseEnterCode);
      return;
    }

    try {
      setIsLoading(true);
      const formattedPhone = formatPhoneWithCountryCode(phone);

      // Verify the phone number with Supabase
      const { error: otpError } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: verificationCode,
        type: 'sms'
      });

      if (otpError) throw otpError;

      // After successful verification, save the phone number to our database
      try {
        // Get the current user again to ensure we have the latest data
        const { data: { user }, error: getUserError } = await supabase.auth.getUser();

        if (getUserError) throw getUserError;

        // Pass both the user ID and the verified phone number
        await updatePhoneNumber(user!.email!, formattedPhone);
        setIsPhoneVerified(true);
        toast.success(t.phoneVerificationSuccess);
      } catch (updateError) {
        console.error('Error saving phone number to database:', updateError);
        // Still mark as verified since the actual verification was successful
        // but let the user know there was an issue with updating the database
        setIsPhoneVerified(true);
        toast.warning(t.phoneUpdateWarning);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error(t.phoneVerificationFailed);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !userData) {
    return <LoadingSpinner />;
  }

  return (
    <Box className="onboarding-container">
      <OnboardingHeader
        title={t.title}
        currentLanguage={currentLanguage}
        changeLanguage={setCurrentLanguage}
      />

      <Stack direction="column" spacing={2} sx={{ maxWidth: 600, mx: 'auto', padding: 4 }}>
        <Box className="main">
          <Typography variant="h4" gutterBottom align="center" sx={{ color: '#C7A46E', mb: 4 }}>
            {t.emailVerified}
          </Typography>

          {!isPhoneVerified ? (
            <Box sx={{ width: '100%' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t.verifyPhone}
              </Alert>

              <TextField
                fullWidth
                label={t.phoneNumber}
                type="tel"
                value={phone}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                margin="normal"
                disabled={isCodeSent}
                placeholder={t.enterPhoneNumber}
                InputLabelProps={{ shrink: true }}
              />

              {!isCodeSent && (
                <Button
                  variant="contained"
                  onClick={sendVerificationCode}
                  disabled={isLoading || !phone}
                  fullWidth
                  sx={{ mt: 2 }}
                  color="primary"
                >
                  {t.sendVerificationCode}
                </Button>
              )}

              {isCodeSent && !isPhoneVerified && (
                <>
                  <TextField
                    fullWidth
                    label={t.verificationCode}
                    value={verificationCode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setVerificationCode(e.target.value)}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />

                  <Button
                    variant="contained"
                    onClick={verifyCode}
                    disabled={isLoading || !verificationCode}
                    fullWidth
                    sx={{ mt: 2 }}
                    color="primary"
                  >
                    {t.verifyCode}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleResendCode}
                    disabled={!canResend || isLoading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {canResend ? t.resendCode : t.resendCodeWithTimer.replace('%s', String(countdown))}
                  </Button>
                </>
              )}
            </Box>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 4 }}>
                {t.phoneVerificationSuccess}
              </Alert>
              <Typography variant="body1" align="center" paragraph>
                {t.contactYou}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 4, display: 'block', mx: 'auto' }}
                href="/"
              >
                {t.goToHome}
              </Button>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default EmailVerificationSuccess;
